import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenStatic } from 'axios';
import { showFatalConnectionError } from './http-error-handler';

/**
 * Extended AxiosInstance that includes CancelToken for convenience
 */
export interface ConfiguredAxiosInstance extends AxiosInstance {
    CancelToken: CancelTokenStatic;
}

/**
 * Status codes that should trigger automatic retry with exponential backoff
 * - 403: Cloudflare challenge / Forbidden (temporary)
 * - 408: Request Timeout
 * - 429: Too Many Requests (rate limiting)
 * - 502: Bad Gateway (temporary gateway issues)
 * - 503: Service Unavailable (temporary unavailability)
 * - 504: Gateway Timeout
 */
const RETRIABLE_STATUS_CODES = [403, 408, 429, 502, 503, 504];

/**
 * Configuration for retry behavior
 */
interface RetryConfig {
    maxRetries?: number;
    baseDelay?: number;
}

/**
 * Track retry attempts for each request
 */
interface RequestRetryInfo {
    retryCount: number;
    maxRetries: number;
}

// Use a Map with request identifier (URL + method) as key to track retries across config objects
const requestRetryMap = new Map<string, RequestRetryInfo>();

/**
 * Generate a unique identifier for a request
 */
function getRequestId(config: AxiosRequestConfig): string {
    const method = (config.method || 'get').toUpperCase();
    const url = config.url || '';
    return `${method}:${url}`;
}

/**
 * Creates a configured axios instance with transient error retry logic
 */
export function createAxiosInstanceWithRetry(retryConfig?: RetryConfig): AxiosInstance {
    const maxRetries = retryConfig?.maxRetries ?? 3;
    const baseDelay = retryConfig?.baseDelay ?? 1000;

    const axiosInstance = axios.create();

    // Request interceptor to track retry attempts
    axiosInstance.interceptors.request.use(
        (config: AxiosRequestConfig) => {
            // Initialize retry tracking for this request if not already tracked
            const requestId = getRequestId(config);
            if (!requestRetryMap.has(requestId)) {
                requestRetryMap.set(requestId, { retryCount: 0, maxRetries });
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor to handle transient errors with retry
    axiosInstance.interceptors.response.use(
        (response: AxiosResponse) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config as AxiosRequestConfig;
            const statusCode = error.response?.status;

            // Retry on retriable status codes
            if (statusCode && RETRIABLE_STATUS_CODES.includes(statusCode) && originalRequest) {
                const requestId = getRequestId(originalRequest);
                
                // Get or initialize retry info for this request
                let retryInfo = requestRetryMap.get(requestId);
                if (!retryInfo) {
                    retryInfo = { retryCount: 0, maxRetries };
                    requestRetryMap.set(requestId, retryInfo);
                }

                if (retryInfo.retryCount < retryInfo.maxRetries) {
                    retryInfo.retryCount++;
                    requestRetryMap.set(requestId, retryInfo);

                    const delay = baseDelay * Math.pow(2, retryInfo.retryCount - 1);
                    console.warn(`${statusCode} error encountered, retry ${retryInfo.retryCount}/${retryInfo.maxRetries} after ${delay}ms`);

                    await new Promise(resolve => setTimeout(resolve, delay));

                    // Retry the request with the same config (axios will handle it)
                    return axiosInstance(originalRequest);
                } else {
                    // Max retries exhausted
                    console.error(`${statusCode} error persisted after ${retryInfo.maxRetries} retries. Showing fatal error message.`);
                    showFatalConnectionError();
                    requestRetryMap.delete(requestId);
                }
            }

            return Promise.reject(error);
        }
    );

    return axiosInstance;
}

// Export a default configured instance for use across ACE
const axiosInstance = createAxiosInstanceWithRetry();

// Expose CancelToken on the configured instance for convenience
// This allows using configuredAxios.CancelToken.source() instead of importing axios separately
(axiosInstance as any).CancelToken = axios.CancelToken;

// Export with proper typing
export const configuredAxios: ConfiguredAxiosInstance = axiosInstance as ConfiguredAxiosInstance;

